import { MarginCategory, RatelistType as RatelistTypeEnum } from "@sinch/core/entities/serverEnums";
import { RatelistType } from "@sinch/core/entities/serverEnums.types";
import { useTranslation } from "react-i18next";
import { useEntityProps } from "../utilsHooks";

/**
 * Single source of truth for how a ratelist type looks in the app: label and title.
 *
 * Use these getters wherever a ratelist type is shown.
 * Never hardcode its label and title in a component, change them here.
 * Every value is shown in the "Entity Identity/RatelistType" story.
 */
export const useRatelistType = () => {
  const { t } = useTranslation(["common", "draft"]);

  const ratelistTypes: Record<
    RatelistType,
    {
      title: string;
      label: string;
      editUri: (id?: string) => string;
      viewUri: (id: string) => string;
      marginCategories: MarginCategory[];
    }
  > = {
    [RatelistTypeEnum.Wage]: {
      title: t("RateBuilder.wagelistTitle"),
      label: t("Ratelist.wagelist"),
      editUri: (id?: string) => `/admin/wagelists/builder${id ? `/${id}` : ""}`,
      viewUri: (id: string) => `/admin/wagelists/builder/${id}/view`,
      marginCategories: [MarginCategory.Attendance],
    },
    [RatelistTypeEnum.Price]: {
      title: t("RateBuilder.pricelistTitle"),
      label: t("Ratelist.pricelist"),
      editUri: (id?: string) => `/admin/pricelists/builder${id ? `/${id}` : ""}`,
      viewUri: (id: string) => `/admin/pricelists/builder/${id}/view`,
      marginCategories: [MarginCategory.Attendance],
    },
    [RatelistTypeEnum.TransportWage]: {
      title: t("RateBuilder.transportWagelistTitle"),
      label: t("Ratelist.transportWagelist"),
      editUri: (id?: string) => `/admin/transport_wagelists/builder${id ? `/${id}` : ""}`,
      viewUri: (id: string) => `/admin/transport_wagelists/builder/${id}/view`,
      marginCategories: [MarginCategory.TransportThere, MarginCategory.TransportBack],
    },
    [RatelistTypeEnum.TransportPrice]: {
      title: t("RateBuilder.transportPricelistTitle"),
      label: t("Ratelist.transportPricelist"),
      editUri: (id?: string) => `/admin/transport_pricelists/builder${id ? `/${id}` : ""}`,
      viewUri: (id: string) => `/admin/transport_pricelists/builder/${id}/view`,
      marginCategories: [MarginCategory.TransportThere, MarginCategory.TransportBack],
    },
    [RatelistTypeEnum.TagWage]: {
      title: "",
      label: t("Ratelist.tagWagelist"),
      editUri: (id?: string) => `/admin/tags/edit/${id}`,
      viewUri: (id: string) => `/admin/tags/view/${id}`,
      marginCategories: [MarginCategory.Tag],
    },
    [RatelistTypeEnum.TagPrice]: {
      title: "",
      label: t("Ratelist.tagPricelist"),
      editUri: (id?: string) => `/admin/tags/edit/${id}`,
      viewUri: (id: string) => `/admin/tags/view/${id}`,
      marginCategories: [MarginCategory.Tag],
    },
  };

  const getProp = useEntityProps(ratelistTypes);

  const getRatelistTypeTitle = getProp<string>("title");
  const getRatelistTypeLabel = getProp<string>("label");
  const getRatelistTypeEditUri = getProp<(id?: string) => string>("editUri");
  const getRatelistTypeViewUri = getProp<(id?: string) => string>("viewUri");
  const getRatelistTypeMarginCategories = getProp<MarginCategory[]>("marginCategories");

  return {
    getRatelistTypeTitle,
    getRatelistTypeEditUri,
    getRatelistTypeMarginCategories,
    getRatelistTypeLabel,
    getRatelistTypeViewUri,
  };
};
